/**
 * dashboardEditChatService.ts — Redux thunks for the dashboard edit-mode chat session.
 *
 * Service layer wrapping DashboardEditChatAPI calls with Redux state management.
 * Components never call DashboardEditChatAPI directly — they dispatch these thunks.
 *
 * Thunks:
 *  - bootstrapEditSessionThunk   — Opens a new edit session for a dashboard.
 *  - sendEditChatMessageThunk    — Sends a user reply to the active edit session.
 *
 * Chat-driven confirmation pivot (2026-05-11): the `applyPendingMutationThunk`
 * and `cancelPendingMutationThunk` were removed. The FE no longer drives
 * apply / cancel via dedicated buttons — the user's chat reply ("yes" /
 * "no" / a new edit intent) flows through the regular send-message path
 * and the BE-side AI decides which tool to call
 * (`confirm_pending_mutation` / `cancel_pending_mutation`).
 *
 * The `mutation_applied` response type is detected here to trigger a
 * `loadPanels` refresh so the canvas reflects the change.
 *
 * Error handling: each thunk dispatches setError on failure. A non-null error
 * in slice state drives the error banner in the ChatOverlay.
 *
 * Idempotency: bootstrapEditSessionThunk guards against double-dispatch —
 * if a session for the same dashboardId already exists in Redux, the thunk
 * is a no-op (the BE would archive the prior session if we called again, which
 * we don't want on close/reopen within the same view).
 *
 * Era: dashboard-era only.
 *
 * TDD ref: plans/2026-05-07-edit-dashboard-via-ai-tdd.md §3.7.2
 */

import { DashboardEditChatAPI } from '@/API/dashboardEditChat'
import type { AppDispatch, RootState } from '@/redux'
import {
  dashboardEditChatActions,
} from '@/redux/slice/dashboardEditChatSlice'
import { loadPanels } from '@/services/DashboardPanelsService'
import { parseAnthropicMessages } from '@/services/utils/parseAnthropicMessages'
import type { ChatMessage } from '@/types/Dashboards.types'

// ─── Error code + HTTP status → user-facing message mapping ─────────────────
//
// Priority: errorCode (from response body's `error` field) → HTTP status →
// fallback to BE-supplied message → generic fallback.
//
// errorCode is set by the API module from `response.data.error`.
// beMessage is the BE-supplied `response.data.message` string (via formatResponse).
// status is the raw HTTP status code.
//
// Supported error codes:
//   DOMAIN_NOT_SUPPORTED — BE owns the copy; render it verbatim if present.
//   ERA_VIOLATION        — residual safety net for the pre-DOMAIN_NOT_SUPPORTED BE.
//   FORBIDDEN            — permission denied (also covers HTTP 403).
//   NOT_FOUND            — dashboard deleted / not found (also covers HTTP 404).
//   SESSION_CONFLICT     — genuine optimistic-concurrency collision (also covers HTTP 409).
//   Network / 5xx        — generic connection error.

const ERROR_CODE_MESSAGES: Record<string, string> = {
  ERA_VIOLATION: "This dashboard isn't supported by Edit mode yet. Try an Enrollment dashboard.",
  FORBIDDEN: "You don't have permission to edit this dashboard.",
  NOT_FOUND: "This dashboard wasn't found. It may have been deleted.",
  SESSION_CONFLICT: 'Another edit session is in progress on this dashboard. Refresh to load the latest state.',
}

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  403: "You don't have permission to edit this dashboard.",
  404: "This dashboard wasn't found. It may have been deleted.",
  409: 'Another edit session is in progress on this dashboard. Refresh to load the latest state.',
}

function resolveErrorMessage(
  err: string | undefined,
  status: number | undefined,
  errorCode?: string | undefined,
): string {
  // 1. Pivot on error code first — most specific.
  if (errorCode) {
    // DOMAIN_NOT_SUPPORTED: use BE-supplied message verbatim (BE owns the copy).
    if (errorCode === 'DOMAIN_NOT_SUPPORTED') {
      return (
        err?.trim() ||
        "Edit mode isn't available for this dashboard yet."
      )
    }
    const codeMessage = ERROR_CODE_MESSAGES[errorCode]
    if (codeMessage) {
      return codeMessage
    }
  }

  // 2. Fall back to HTTP status.
  if (status != null && HTTP_STATUS_MESSAGES[status]) {
    return HTTP_STATUS_MESSAGES[status]
  }

  // 3. Network / generic 5xx: no response body or 5xx range.
  if (status == null || status >= 500) {
    return "Couldn't reach the server. Check your connection and try again."
  }

  // 4. Last resort: use BE-supplied error message or generic fallback.
  return err ?? 'Something went wrong. Please try again.'
}

// ─── bootstrapEditSessionThunk ────────────────────────────────────────────────

/**
 * Opens a new edit session for the given dashboard.
 *
 * Called by AskTcsAiToggle when the user opens the overlay AND no active
 * edit session exists for the current dashboard. Subsequent opens of the
 * same overlay during the same dashboard view are no-ops (AC #6 — session
 * persists across overlay open/close cycles).
 *
 * On success: dispatches hydrateEditSession with the bootstrap response.
 * On non-empty archivedPriorSessionIds: logs a console.info (no UI per scope).
 * On error: dispatches setError with a human-readable message.
 */
export const bootstrapEditSessionThunk = (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // Guard: short-circuit if a bootstrap is already in-flight OR a session
    // already exists for this exact dashboard.
    //
    // Two cases handled:
    //
    // (1) Concurrent bootstrap race — Bug 19's belt-and-suspenders useEffect
    //     can fire twice in quick succession (once when flowState transitions
    //     to 'complete', once when creationStatus transitions to 'complete').
    //     Both calls pass the sessionId guard simultaneously because the slice
    //     is still empty for both. The first call sets isBootstrapping = true
    //     synchronously via setBootstrapping, so the second call sees it here
    //     and bails before issuing a duplicate POST.
    //
    // (2) Close-overlay / reopen-overlay cycle (AC #6) — once the first
    //     bootstrap has completed and sessionId is populated, subsequent calls
    //     for the same dashboard short-circuit on the second condition.
    const existing = getState().dashboardEditChat
    if (existing.isBootstrapping) {
      return  // a concurrent bootstrap is already in-flight; let it complete
    }
    if (existing.sessionId !== null && existing.dashboardId === dashboardId) {
      return  // already bootstrapped for this dashboard
    }

    dispatch(dashboardEditChatActions.setBootstrapping({ dashboardId }))

    const resp = await DashboardEditChatAPI.bootstrapEditSession(token, dashboardId)

    // Discard stale response if the user navigated to a different dashboard
    // while the bootstrap was in-flight.
    const currentDashboardId = getState().dashboards.selectedDashboard?.id
    if (currentDashboardId !== undefined && currentDashboardId !== dashboardId) {
      dispatch(dashboardEditChatActions.setError(null))
      return
    }

    if (resp.err || !resp.data) {
      dispatch(
        dashboardEditChatActions.setError(
          resolveErrorMessage(resp.err, resp.status, resp.errorCode)
        )
      )
      return
    }

    const {
      sessionId,
      initialFlowState,
      initialFlowContext,
      archivedPriorSessionIds,
      priorMessages,
    } = resp.data

    // AC #2: log archived prior sessions; no UI required in v1.
    if (archivedPriorSessionIds.length > 0) {
      console.info(
        `[dashboardEditChat] bootstrapEditSession: archived ${archivedPriorSessionIds.length} prior session(s) for dashboard ${dashboardId}:`,
        archivedPriorSessionIds
      )
    }

    // Restore prior chat history when the BE forwarded messages from the
    // archived session (browser refresh path). parseAnthropicMessages converts
    // raw Anthropic-format objects to typed ChatMessage[] — the same parser
    // used by loadChatSession for the create-mode thread.
    //
    // extractedCredentials is a create-mode concern; discard it here.
    const restoredMessages: ChatMessage[] =
      priorMessages?.length > 0
        ? parseAnthropicMessages(priorMessages).messages
        : []

    dispatch(
      dashboardEditChatActions.hydrateEditSession({
        sessionId,
        flowState: initialFlowState,
        flowContext: initialFlowContext,
        messages: restoredMessages,
      })
    )
  }

// ─── sendEditChatMessageThunk ─────────────────────────────────────────────────

/**
 * Sends a user message to the active edit session and stores both the user
 * bubble and the assistant reply on `dashboardEditChatSlice.messages`.
 *
 * Reads `dashboardId` + `sessionId` from the slice — the component just
 * supplies the message text. When the response is `mutation_proposed` /
 * `mutation_cancelled` / `mutation_applied`, the carried `flowState` and
 * `flowContext` are applied to the slice. On `mutation_applied` we also
 * trigger `loadPanels` so the canvas reflects the change.
 *
 * Routing rule (era axiom): the edit-mode chat path goes through
 * `DashboardEditChatAPI.sendChatMessage` which targets
 * `POST /v1/dashboards/:id/edit-sessions/:sessionId/messages`. The legacy
 * `sendChatMessage` thunk (DashboardChatService.ts) stays on
 * `POST /v1/dashboards/:id/chat` for create-mode sessions. The two paths
 * do NOT share state.
 */
export const sendEditChatMessageThunk = (token: string, messageText: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    const { sessionId, dashboardId } = getState().dashboardEditChat
    if (!sessionId || !dashboardId) {
      dispatch(dashboardEditChatActions.setError(
        'Cannot send message: edit session not active.'
      ))
      return false
    }

    // Optimistic user bubble
    const userMsg: ChatMessage = {
      role: 'user',
      type: 'text',
      content: messageText,
      timestamp: new Date().toISOString(),
    }
    dispatch(dashboardEditChatActions.appendEditMessage(userMsg))
    dispatch(dashboardEditChatActions.setIsSending(true))
    dispatch(dashboardEditChatActions.setError(null))

    try {
      const resp = await DashboardEditChatAPI.sendChatMessage(
        token,
        dashboardId,
        sessionId,
        messageText
      )

      // Discard stale response if user navigated to a different dashboard.
      const currentId = getState().dashboards.selectedDashboard?.id
      if (currentId !== undefined && currentId !== dashboardId) return false

      if (resp.err || !resp.data) {
        dispatch(
          dashboardEditChatActions.setError(
            resolveErrorMessage(resp.err, resp.status, resp.errorCode)
          )
        )
        return false
      }

      const { message } = resp.data

      // Append assistant reply to the edit thread.
      //
      // mutation_* types are rendered as plain text — they don't get a bespoke
      // card (chat-driven confirmation pivot, 2026-05-11).
      //
      // edit_filter_card preserves its type so ChatPanel renders the inline
      // filter card (Epic 3 §7.2). It also carries flowState + flowContext +
      // editFilterFields — all three are stored on the ChatMessage.
      //
      // query_panel_data_result preserves its type so ChatPanel renders the
      // truncation banner when truncated:true. The qaToolResult metadata is
      // passed through from the BE response. Track 3 Slice 2.2.
      const renderType: ChatMessage['type'] =
        message.type === 'mutation_proposed' ||
        message.type === 'mutation_cancelled' ||
        message.type === 'mutation_applied'
          ? 'text'
          : (message.type as ChatMessage['type'])

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        type: renderType,
        content: message.content,
        timestamp: message.timestamp,
        // edit_filter_card fields (only present when type === 'edit_filter_card')
        ...(message.type === 'edit_filter_card' && message.editFilterFields != null
          ? { editFilterFields: message.editFilterFields }
          : {}),
        // Q&A tool result metadata — present on both query_panel_data_result
        // AND aggregate_panel_data_result. Pre-round-2 the spread only
        // handled the query variant, so AggregateOtherBucketBanner never
        // rendered on a live aggregate turn (only after session-restore).
        // Round 2 fix for Claude / Copilot / Qodo on PR #389.
        ...((message.type === 'query_panel_data_result' ||
          message.type === 'aggregate_panel_data_result') &&
        message.qaToolResult != null
          ? { qaToolResult: message.qaToolResult }
          : {}),
      }
      dispatch(dashboardEditChatActions.appendEditMessage(assistantMsg))

      // Sync FSM state when the response carries it (mutation_* types and
      // edit_filter_card — the latter transitions FSM to awaiting_filters).
      if (message.flowState && message.flowContext) {
        dispatch(
          dashboardEditChatActions.updateSessionFromMessageResponse({
            flowState: message.flowState,
            flowContext: message.flowContext,
          })
        )
      }

      // mutation_applied flows the dashboard mutation through (add / remove /
      // swap / modify / reorder / rename / change_filter_values). Reload the
      // panel list so the canvas reflects the new state. When the apply also
      // enqueues a sync (change_filter_values), the reload surfaces a fresh
      // first-paint immediately; sockets handle the post-sync refresh.
      if (message.type === 'mutation_applied') {
        dispatch(loadPanels(token, dashboardId))
      }

      return true
    } catch {
      dispatch(dashboardEditChatActions.setError('Failed to send message. Please try again.'))
      return false
    } finally {
      dispatch(dashboardEditChatActions.setIsSending(false))
    }
  }
